import { useState } from 'react';
import axios from 'axios';
import { UploadCloud, File, CheckCircle, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function Upload() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [tableName, setTableName] = useState('');
  const [previewData, setPreviewData] = useState(null); // { columns: [], filename: '' }
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // --- HANDLERS FOR STEP 1 (UPLOAD) ---
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleFileSelection = (selectedFile) => {
    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      toast.error('Doar fisiere Excel (.xlsx, .xls) sunt permise!');
      return;
    }
    setFile(selectedFile);
    // Auto-fill table name from filename
    const name = selectedFile.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9]/g, "_");
    setTableName(name);
  };

  const handlePreview = async () => {
    if (!file || !tableName) {
      toast.error('Selecteaza un fisier si introdu numele tabelei!');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/tables/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(response.data);
      setStep(2);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Eroare la parsarea fisierului');
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLERS FOR STEP 2 (SCHEMA EDIT) ---
  const handleTypeChange = (colIndex, newType) => {
    const updatedCols = [...previewData.columns];
    updatedCols[colIndex].type = newType;
    setPreviewData({ ...previewData, columns: updatedCols });
  };

  // --- HANDLERS FOR STEP 3 (CONFIRM) ---
  const handleCreate = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('table_name', tableName);
    formData.append('schema_json', JSON.stringify(previewData.columns));

    try {
      const response = await axios.post(`${API}/tables/create`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      // Reset form
      setFile(null);
      setTableName('');
      setPreviewData(null);
      setStep(1);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Eroare la crearea tabelei');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Incarcare Fisiere
        </h1>
        <p className="text-gray-400">
          {step === 1 && 'Pasul 1: Incarca fisierul si numeste tabela'}
          {step === 2 && 'Pasul 2: Verifica si editeaza structura coloanelor'}
          {step === 3 && 'Pasul 3: Confirmare finala'}
        </p>
      </div>

      <div className="max-w-4xl mx-auto bg-[#18181b] rounded-xl border border-white/10 p-8">

        {/* STEP 1: UPLOAD */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nume Tabela (Obligatoriu)</label>
              <Input
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="ex: vanzari_2024"
                className="bg-[#27272a] border-white/20 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Acesta va fi numele tabelei in baza de date.</p>
            </div>

            <div
              className={`upload-zone border-2 border-dashed rounded-xl p-12 text-center transition-colors ${dragActive ? 'border-cyan-400 bg-cyan-400/5' : 'border-white/20 hover:border-white/40'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input type="file" id="file-upload" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} />
              <label htmlFor="file-upload" className="cursor-pointer block">
                <UploadCloud size={64} className="mx-auto mb-4 text-cyan-400" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {file ? file.name : 'Trage fisiere aici sau click pentru a selecta'}
                </h3>
                <p className="text-sm text-gray-400">Suporta .xlsx si .xls</p>
              </label>
            </div>

            <Button
              onClick={handlePreview}
              disabled={!file || !tableName || loading}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              {loading ? 'Se analizeaza...' : 'Pasul Urmator: Previzualizare'} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* STEP 2: SCHEMA EDIT */}
        {step === 2 && previewData && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Coloane Identificate ({previewData.columns.length})</h3>
              <span className="text-sm text-gray-400">Verifica tipurile de date</span>
            </div>

            <div className="border rounded-md border-white/10 overflow-hidden">
              <Table>
                <TableHeader className="bg-[#27272a]">
                  <TableRow>
                    <TableHead className="text-white">Nume Coloana Excel</TableHead>
                    <TableHead className="text-white">Nume in DB</TableHead>
                    <TableHead className="text-white">Tip Detectat</TableHead>
                    <TableHead className="text-white">Exemplu Valoare</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.columns.map((col, idx) => (
                    <TableRow key={idx} className="border-white/5">
                      <TableCell className="font-medium text-gray-200">{col.name}</TableCell>
                      <TableCell className="text-gray-400">{col.new_name}</TableCell>
                      <TableCell>
                        <Select
                          defaultValue={col.type}
                          onValueChange={(val) => handleTypeChange(idx, val)}
                        >
                          <SelectTrigger className="w-[180px] h-8 bg-[#09090b] border-white/10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#18181b] border-white/10 text-white">
                            <SelectItem value="TEXT">TEXT (String)</SelectItem>
                            <SelectItem value="BIGINT">NUMAR INTREG</SelectItem>
                            <SelectItem value="NUMERIC(19, 4)">NUMAR (Zecimal)</SelectItem>
                            <SelectItem value="TIMESTAMP">DATA / TIMP</SelectItem>
                            <SelectItem value="DATE">DATA</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm truncate max-w-[150px]">{col.sample}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(1)} className="border-white/20 text-white">
                <ArrowLeft className="mr-2 h-4 w-4" /> Inapoi
              </Button>
              <Button onClick={handleCreate} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                {loading ? 'Se salveaza...' : 'Salveaza si Creeaza Tabela'} <CheckCircle className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}